// src/services/reportSearchPatients.js
//
// Who the weekly report's patient search can find: the same patients the
// Coordinator (CRC) page counts at the top (2026-10-09), i.e.
//   - consents with a `signedAt` (the CRC page lists them ordered by signedAt,
//     which leaves out a consent without one), and
//   - on the legacy-Sheet site (Dubois), the Sheet's patients.
// Potential duplicates (`duplicates`) are NOT searchable: on the CRC page they
// are only a separate tile and tab, not part of the patient list. A Sheet
// patient whose status is "Potential Duplicate" is left out the same way
// (2026-10-29), as the CRC page now shows it in the Duplicates tab.
//
// The CRC page's "Scan for Duplicates" (DuplicateScanPage) scans this same list.
//
// The study page's "Find Potential Patients" keeps using
// loadPatientsForMatching (studyScreeningService.js), unchanged.
//
// The Sheet is fail-soft, as before: if it can't be read, the RegX patients
// still load and `sheetError` says the list may be short.

import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { db } from "../firebase";
import { getConsentsPath } from "./firestorePaths";
import { activeSiteUsesLegacySheet } from "./activeTenant";
import { fetchSheetPatients } from "./sheetPatientsService";
import { normalizePatientForMatching } from "./eligibilityMatch";
import { isPotentialDuplicateStatus } from "../constants/patientBoxes";

/** @returns {Promise<{ patients: object[], sheetError: string }>} */
export const loadReportSearchPatients = async () => {
  const today = new Date();
  const consSnap = await getDocs(query(collection(db, getConsentsPath()), orderBy("signedAt", "desc")));
  const patients = consSnap.docs.map((d) => normalizePatientForMatching({ id: d.id, ...d.data() }, "firestore", today));

  let sheetError = "";
  if (activeSiteUsesLegacySheet()) {
    try {
      const rows = await fetchSheetPatients();
      rows
        .filter((r) => !isPotentialDuplicateStatus(r.status))
        .forEach((r) => patients.push(normalizePatientForMatching(r, "sheet", today)));
    } catch (e) {
      sheetError = e.message || "The legacy sheet could not be read.";
    }
  }
  return { patients, sheetError };
};
